/**
 * useCalcProcess
 *
 * Owns performCalcProcess, previously a class method on FormViewer.
 * Called back from both useVerify (via verifyCallbacksRef) and
 * useFormFieldEvents (via fieldEventsCallbacksRef).
 *
 *   performCalcProcess(aForm, xIsAssetForm, targetId, targetValue, calcTypeEnum, vfyTab)
 *     — triggers ReturnCalc and, when verify is dirty, re-runs verification
 *       after a short delay.
 *
 * Returns:
 *   { performCalcProcess }
 */
import { useCallback } from 'react';

const useCalcProcess = ({
  calcAPIRef,
  verifyRef, // kept-current ref to useVerify result
}) => {
  /**
   * Runs ReturnCalc then, if verification was previously dirty, re-runs
   * verifyReturn after a 1-second delay and clears the dirty flag.
   *
   * @param {string}  aForm         active form name
   * @param {string}  xIsAssetForm  'X' if asset form, else ''
   * @param {string}  targetId      focused field ID
   * @param {string}  targetValue   focused field value
   * @param {number}  calcTypeEnum  CALC_TYPE constant
   * @param {string}  [vfyTab]      verify tab to open after re-verify
   */
  const performCalcProcess = useCallback(
    (aForm, xIsAssetForm, targetId, targetValue, calcTypeEnum, vfyTab) => {
      calcAPIRef.current.ReturnCalc(aForm, xIsAssetForm, targetId, targetValue, calcTypeEnum);
      const verify = verifyRef.current;
      if (verify.verifyDirty) {
        verify.setVerifyDirty(false);
        setTimeout(() => {
          verify.verifyReturn(vfyTab);
        }, 1000);
      }
    },
    [calcAPIRef, verifyRef],
  );

  return { performCalcProcess };
};

export default useCalcProcess;
